import { create } from 'zustand';

import { lookupRegionHazards } from '@/data/region-hazards';
import type {
  Basement,
  ClimateZone,
  ExteriorMaterial,
  Foundation,
  Garage,
  HomeType,
  HvacType,
  LotBand,
  RegionHazard,
  RoofMaterial,
  Sewer,
  SetupMode,
  WaterSource,
  YearBand,
} from '@/db/types';

/**
 * Inside / outside selections are quantity-aware: a user can have two
 * fireplaces, three ceiling fans, two snow blowers, etc. Each tile carries
 * a count (0 = not selected, 1+ = selected with quantity). Persist will
 * fan out to N system rows per kind.
 */
export type QtyMap = Record<string, number>;

export type HeatingFuel = 'gas' | 'electric' | 'oil' | 'propane' | 'dual-fuel' | 'unsure';

export interface OnboardingState {
  mode: SetupMode;
  /** True when the user reached this flow via /add-home (i.e. they
   *  already have at least one property). The onboarding layout uses
   *  this to show an X (exit-to-My-Home) button on the welcome screen
   *  instead of hiding the back/exit affordance entirely — the first
   *  install genuinely has nowhere to go back to, but an add-home
   *  flow always has a home to return to. */
  isAddingHome: boolean;
  ownerFirstName: string;
  ownerLastName: string;
  /** What the user calls their home — e.g. "Lakehouse", "The bungalow",
   *  "405 Maple". Powers the greeting (when greeting_source is
   *  'home_name') and the desktop sidebar nickname card. Optional —
   *  if blank we fall back to "My home" in surface copy. */
  homeName: string;
  zip: string;
  address: string | null;
  lat: number | null;
  lon: number | null;
  hasPool: boolean;
  hasSolar: boolean;
  hasPets: boolean;
  hasKids: boolean;
  hasHoa: boolean;
  hasWoodfire: boolean;
  hasSmartHome: boolean;
  hasStrHost: boolean;
  hasHeavySnow: boolean;
  hasGarden: boolean;
  hasAccessibility: boolean;
  isFirstTime: boolean;
  // v35 lifestyle additions. Each unlocks a specific bundle of tasks.
  travelsOften: boolean;
  worksFromHome: boolean;
  hasAllergies: boolean;
  hasNewborn: boolean;
  multiGenHousehold: boolean;
  over60Owner: boolean;
  hasLivestock: boolean;
  hasShortTermContractors: boolean;
  /** Regional natural-hazard tags (hurricane, tornado, etc). Pre-filled
   *  from ZIP via lookupRegionHazards on the lifestyle step; user can
   *  refine via chips. Schema v35 → property.region_hazards_json. */
  regionHazards: RegionHazard[];
  /** True once the user has tapped a hazard chip. Stops the ZIP
   *  pre-fill from clobbering their explicit choice when they navigate
   *  back to the property step. */
  regionHazardsTouched: boolean;
  climateZone: ClimateZone | null;
  climateLabel: string | null;
  homeType: HomeType | null;
  yearBand: YearBand | null;
  /** Specific build year captured on step 2 (home-type) of onboarding.
   *  Optional but heavily encouraged — replaces the vague year_band
   *  bucket for surfaces that want a precise number. */
  yearBuilt: number | null;
  sqft: number;
  hvacType: HvacType | null;
  /** Primary heating fuel. Paired with HVAC type on the same onboarding
   *  step — e.g. a furnace can be gas/oil/propane/electric, a heat pump
   *  is electric, a boiler is often oil or gas. Schema v35. */
  heatingFuel: HeatingFuel | null;
  /** Year the user took ownership of the home. Drives onboarding tone
   *  (fresh-owner discovery vs long-term tracker). Optional. Schema v35. */
  ownedSince: number | null;
  lotBand: LotBand | null;
  bedrooms: number | null;
  bathrooms: number | null;
  /** Number of above-grade stories (1, 2, 3, etc.). Drives roof
   *  inspection difficulty hints and structural-walk tasks. */
  stories: number | null;
  basement: Basement | null;
  foundation: Foundation | null;
  roofMaterial: RoofMaterial | null;
  exteriorMaterial: ExteriorMaterial | null;
  // Multi-value extensions added with schema v32. Real homes are often
  // mixed (brick front + vinyl sides, main asphalt + flat porch roof,
  // split-level basement + slab). When set, these arrays are the
  // canonical source; the legacy single-value fields above mirror the
  // first element for back-compat with older code paths and exports.
  roofMaterials: RoofMaterial[];
  exteriorMaterials: ExteriorMaterial[];
  foundations: Foundation[];
  garage: Garage | null;
  waterSource: WaterSource | null;
  sewer: Sewer | null;
  inside: QtyMap;
  outside: QtyMap;

  setMode: (m: SetupMode) => void;
  setOwnerName: (first: string, last: string) => void;
  setHomeName: (name: string) => void;
  setZip: (zip: string, zone?: ClimateZone | null, label?: string | null) => void;
  setAddress: (address: string | null, lat: number | null, lon: number | null) => void;
  setLifestyle: (
    key:
      | 'hasPool' | 'hasSolar' | 'hasPets' | 'hasKids'
      | 'hasHoa' | 'hasWoodfire' | 'hasSmartHome' | 'hasStrHost'
      | 'hasHeavySnow' | 'hasGarden' | 'hasAccessibility' | 'isFirstTime'
      | 'travelsOften' | 'worksFromHome' | 'hasAllergies' | 'hasNewborn'
      | 'multiGenHousehold' | 'over60Owner' | 'hasLivestock'
      | 'hasShortTermContractors',
    value: boolean
  ) => void;
  setHomeType: (t: HomeType) => void;
  setYearBand: (y: YearBand) => void;
  setYearBuilt: (y: number | null) => void;
  setSqft: (n: number) => void;
  setHvac: (h: HvacType) => void;
  setHeatingFuel: (f: HeatingFuel | null) => void;
  setOwnedSince: (y: number | null) => void;
  setLotBand: (l: LotBand) => void;
  setBedrooms: (n: number | null) => void;
  setBathrooms: (n: number | null) => void;
  setStories: (n: number | null) => void;
  setBasement: (b: Basement | null) => void;
  setFoundation: (f: Foundation | null) => void;
  setRoofMaterial: (r: RoofMaterial | null) => void;
  setExteriorMaterial: (e: ExteriorMaterial | null) => void;
  setRoofMaterials: (xs: RoofMaterial[]) => void;
  setExteriorMaterials: (xs: ExteriorMaterial[]) => void;
  setFoundations: (xs: Foundation[]) => void;
  setGarage: (g: Garage | null) => void;
  setWaterSource: (w: WaterSource | null) => void;
  setSewer: (s: Sewer | null) => void;
  setRegionHazards: (xs: RegionHazard[]) => void;
  toggleRegionHazard: (h: RegionHazard) => void;
  /** Pre-fill hazards from the user's ZIP. No-op once the user has
   *  tapped a chip (regionHazardsTouched=true) so a back-nav doesn't
   *  overwrite their choice. */
  applyRegionHazardDefaults: () => void;
  setInsideCount: (k: string, n: number) => void;
  setOutsideCount: (k: string, n: number) => void;
  toggleInside: (k: string) => void;
  toggleOutside: (k: string) => void;
  /** Apply smart defaults for the inside step (water heater, fridge,
   *  disp, dw, laundry, range, smoke, bath fan). No-op if the user has
   *  already touched the inside map — we don't want to overwrite their
   *  choices when they come back via the back button. */
  applyInsideDefaults: () => void;
  /** Apply smart defaults for the outside step based on the home type
   *  the user picked. Same no-op-on-touch behavior as inside. */
  applyOutsideDefaults: () => void;
  /** Apply smart chip defaults on the Detail screen based on
   *  home_type + year_band. Only fills fields the user hasn't already
   *  set. */
  applyDetailDefaults: () => void;
  reset: () => void;
  /** Clear all per-property state but keep owner identity. Used when
   *  kicking off the add-home flow so the 2nd / 3rd / Nth home starts
   *  fresh without forcing the user to re-type their name. */
  resetForNewHome: (firstName: string, lastName: string) => void;
}

const initial = {
  mode: 'quick' as SetupMode,
  isAddingHome: false,
  ownerFirstName: '',
  ownerLastName: '',
  homeName: '',
  zip: '',
  address: null as string | null,
  lat: null as number | null,
  lon: null as number | null,
  hasPool: false,
  hasSolar: false,
  hasPets: false,
  hasKids: false,
  hasHoa: false,
  hasWoodfire: false,
  hasSmartHome: false,
  hasStrHost: false,
  hasHeavySnow: false,
  hasGarden: false,
  hasAccessibility: false,
  isFirstTime: false,
  travelsOften: false,
  worksFromHome: false,
  hasAllergies: false,
  hasNewborn: false,
  multiGenHousehold: false,
  over60Owner: false,
  hasLivestock: false,
  hasShortTermContractors: false,
  regionHazards: [] as RegionHazard[],
  regionHazardsTouched: false,
  climateZone: null,
  climateLabel: null,
  homeType: null,
  yearBand: null,
  yearBuilt: null as number | null,
  sqft: 2400,
  hvacType: null,
  heatingFuel: null as HeatingFuel | null,
  ownedSince: null as number | null,
  lotBand: null,
  // Sensible defaults so the user doesn't have to tap chips for the
  // most common layout (3-bed, 2-bath, 1-story single-family). They can
  // override on the property step or via Tune my plan later. Picking 0
  // / null here would have left these blank in profile completeness
  // and forced an extra walkthrough card just to confirm typical
  // numbers. 3-2-1 covers the median US single-family home; townhouse
  // and condo users will adjust on the way in.
  bedrooms: 3 as number | null,
  bathrooms: 2 as number | null,
  stories: 1 as number | null,
  basement: null as Basement | null,
  foundation: null as Foundation | null,
  roofMaterial: null as RoofMaterial | null,
  exteriorMaterial: null as ExteriorMaterial | null,
  roofMaterials: [] as RoofMaterial[],
  exteriorMaterials: [] as ExteriorMaterial[],
  foundations: [] as Foundation[],
  garage: null as Garage | null,
  waterSource: null as WaterSource | null,
  sewer: null as Sewer | null,
  inside: {} as QtyMap,
  outside: {} as QtyMap,
};

/**
 * Inside systems that almost every US home has. Pre-selected on the
 * onboarding inside step so new users don't have to tap through the
 * obvious choices. They can de-select with one tap if any don't apply
 * (rare apartments without disposals, kitchenettes without ranges, etc).
 *
 * Order matches the tile grid for clarity.
 */
const INSIDE_DEFAULTS: string[] = [
  'wh',        // Water heater — every home
  'fridge',    // Refrigerator — every home
  'range',     // Range / oven — every home
  'disp',      // Garbage disposal — ~75% of US homes
  'dw',        // Dishwasher — ~70%
  'laundry',   // Washer & dryer — most homes have one
  'smoke',     // Smoke / CO alarms — required by code in every state
  'bathfan',   // Bath exhaust fan — required by code in new builds, common everywhere
];

/**
 * Outside systems by home type. House owners get the full yard set;
 * townhouses get a subset; condos get none (they're interior-only by
 * definition). Mobile homes get a small set focused on what their
 * structure typically has.
 */
const OUTSIDE_DEFAULTS_BY_TYPE: Record<HomeType, string[]> = {
  house: ['lawn', 'woods', 'gutters', 'driveway', 'siding', 'outdoor_faucets'],
  town: ['gutters', 'siding', 'outdoor_faucets'],
  condo: [],
  mobile: ['lawn', 'gutters', 'driveway', 'siding', 'outdoor_faucets'],
};

function setCount(map: QtyMap, k: string, n: number): QtyMap {
  const clamped = Math.max(0, Math.min(99, n));
  const next = { ...map };
  if (clamped === 0) {
    delete next[k];
  } else {
    next[k] = clamped;
  }
  return next;
}

function toggle(map: QtyMap, k: string): QtyMap {
  return setCount(map, k, (map[k] ?? 0) > 0 ? 0 : 1);
}

export const useOnboarding = create<OnboardingState>((set) => ({
  ...initial,

  setMode: (mode) => set({ mode }),
  setOwnerName: (ownerFirstName, ownerLastName) =>
    set({ ownerFirstName, ownerLastName }),
  setHomeName: (homeName) => set({ homeName }),
  setZip: (zip, climateZone = null, climateLabel = null) =>
    set({ zip, climateZone, climateLabel }),
  setAddress: (address, lat, lon) => set({ address, lat, lon }),
  setLifestyle: (key, value) => set({ [key]: value } as Partial<OnboardingState>),
  setHomeType: (homeType) => set({ homeType }),
  setYearBand: (yearBand) => set({ yearBand }),
  setYearBuilt: (yearBuilt) => set({ yearBuilt }),
  setSqft: (sqft) => set({ sqft }),
  setHvac: (hvacType) => set({ hvacType }),
  setHeatingFuel: (heatingFuel) => set({ heatingFuel }),
  setOwnedSince: (ownedSince) => set({ ownedSince }),
  setLotBand: (lotBand) => set({ lotBand }),
  setBedrooms: (bedrooms) => set({ bedrooms }),
  setBathrooms: (bathrooms) => set({ bathrooms }),
  setStories: (stories) => set({ stories }),
  setBasement: (basement) => set({ basement }),
  setFoundation: (foundation) => set({ foundation }),
  setRoofMaterial: (roofMaterial) => set({ roofMaterial }),
  setExteriorMaterial: (exteriorMaterial) => set({ exteriorMaterial }),
  // Multi-value setters keep the legacy single-value column in sync
  // with the first array element so existing read paths (PDF binder,
  // task-rules check that doesn't yet read the array, etc.) keep
  // working. Empty array clears the legacy column too.
  setRoofMaterials: (xs) => set({
    roofMaterials: xs,
    roofMaterial: xs[0] ?? null,
  }),
  setExteriorMaterials: (xs) => set({
    exteriorMaterials: xs,
    exteriorMaterial: xs[0] ?? null,
  }),
  setFoundations: (xs) => set({
    foundations: xs,
    foundation: xs[0] ?? null,
  }),
  setGarage: (garage) => set({ garage }),
  setWaterSource: (waterSource) => set({ waterSource }),
  setSewer: (sewer) => set({ sewer }),

  setRegionHazards: (regionHazards) =>
    set({ regionHazards, regionHazardsTouched: true }),
  toggleRegionHazard: (h) =>
    set((s) => {
      const has = s.regionHazards.includes(h);
      const next = has
        ? s.regionHazards.filter((x) => x !== h)
        : [...s.regionHazards, h];
      return { regionHazards: next, regionHazardsTouched: true };
    }),
  applyRegionHazardDefaults: () =>
    set((s) => {
      if (s.regionHazardsTouched) return s;
      if (!s.zip) return s;
      const defaults = lookupRegionHazards(s.zip);
      if (defaults.length === 0) return s;
      return { regionHazards: defaults };
    }),

  setInsideCount: (k, n) => set((s) => ({ inside: setCount(s.inside, k, n) })),
  setOutsideCount: (k, n) => set((s) => ({ outside: setCount(s.outside, k, n) })),
  toggleInside: (k) => set((s) => ({ inside: toggle(s.inside, k) })),
  toggleOutside: (k) => set((s) => ({ outside: toggle(s.outside, k) })),

  applyInsideDefaults: () =>
    set((s) => {
      // Bail if user has already interacted — Object.keys check rather
      // than qtyTotal() so a count of 0 (user cleared a default they
      // didn't want) still counts as "touched."
      if (Object.keys(s.inside).length > 0) return s;
      const filled: QtyMap = {};
      for (const k of INSIDE_DEFAULTS) filled[k] = 1;
      // Single-family houses get a water softener pre-checked too —
      // most US single-family installs are on city water that hits a
      // softener. Townhomes / condos / mobile usually share a building
      // softener (or aren't soft enough to need one) so we leave those
      // user-driven.
      if (s.homeType === 'house') filled['water_softener'] = 1;
      return { inside: filled };
    }),

  applyOutsideDefaults: () =>
    set((s) => {
      if (Object.keys(s.outside).length > 0) return s;
      const defaults = s.homeType
        ? OUTSIDE_DEFAULTS_BY_TYPE[s.homeType]
        : [];
      if (defaults.length === 0) return s;
      const filled: QtyMap = {};
      for (const k of defaults) filled[k] = 1;
      return { outside: filled };
    }),

  applyDetailDefaults: () =>
    set((s) => {
      // Smart chip defaults based on home_type + year_band. Each field
      // only fills if the user hasn't already picked something — never
      // overwrite a deliberate choice.
      //
      // Reasoning baked in:
      //   - Roof / Exterior: asphalt + vinyl are the most common in
      //     the US (~70% and ~30% respectively). Safe blind guesses.
      //   - Water / Sewer: city is ~83% / ~75%.
      //   - Basement + Foundation vary by home_type:
      //       house: usually basement+basement on older builds,
      //         shifting to slab in 2010+ in much of the country.
      //       town / condo / mobile: slab/pier dominant.
      //   - Garage: scaled by home_type.
      const next: Partial<OnboardingState> = {};
      if (!s.roofMaterial && s.roofMaterials.length === 0) {
        next.roofMaterial = 'asphalt';
        next.roofMaterials = ['asphalt'];
      }
      if (!s.exteriorMaterial && s.exteriorMaterials.length === 0) {
        next.exteriorMaterial = 'vinyl';
        next.exteriorMaterials = ['vinyl'];
      }
      if (!s.waterSource) next.waterSource = 'city';
      if (!s.sewer) next.sewer = 'city';

      if (!s.basement) {
        if (s.homeType === 'house' && s.yearBand === '2010+') next.basement = 'none';
        else if (s.homeType === 'house') next.basement = 'unfinished';
        else if (s.homeType === 'town') next.basement = 'none';
        else if (s.homeType === 'condo') next.basement = 'none';
        else if (s.homeType === 'mobile') next.basement = 'none';
      }
      if (!s.foundation && s.foundations.length === 0) {
        let guess: Foundation | null = null;
        if (s.homeType === 'house' && s.yearBand === '2010+') guess = 'slab';
        else if (s.homeType === 'house') guess = 'basement';
        else if (s.homeType === 'town') guess = 'slab';
        else if (s.homeType === 'condo') guess = 'slab';
        else if (s.homeType === 'mobile') guess = 'pier';
        if (guess) {
          next.foundation = guess;
          next.foundations = [guess];
        }
      }
      if (!s.garage) {
        if (s.homeType === 'house') next.garage = '2-car';
        else if (s.homeType === 'town') next.garage = '1-car';
        else next.garage = 'none';
      }
      return next;
    }),

  reset: () => set({ ...initial, inside: {}, outside: {} }),

  /**
   * Multi-home variant of reset. Clears all property-scoped state so the
   * second / third / Nth home onboarding starts fresh, but PRESERVES the
   * owner's name — that's an account-level attribute, not per-home. We
   * also reset mode to null-ish so the path screen forces the user to
   * pick Quick or Full again instead of silently inheriting the
   * previous home's choice (different mental contexts for different
   * homes — vacation rental might warrant Full while a condo wants Quick).
   */
  resetForNewHome: (firstName: string, lastName: string) =>
    set({
      ...initial,
      inside: {},
      outside: {},
      ownerFirstName: firstName,
      ownerLastName: lastName,
      // Flag the add-home flow so the onboarding layout swaps the
      // hidden back/exit for a visible X button on the welcome step.
      isAddingHome: true,
    }),
}));

/** Total instances across all selected kinds — used for the count badges. */
export function qtyTotal(map: QtyMap): number {
  let n = 0;
  for (const k of Object.keys(map)) n += map[k] ?? 0;
  return n;
}

/** Kinds with at least one instance — used for "which kinds present" checks. */
export function qtyKinds(map: QtyMap): string[] {
  return Object.entries(map)
    .filter(([, n]) => n > 0)
    .map(([k]) => k);
}
